import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ChevronRight, Lock, FileSignature } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DocumentBody from "@/components/public/DocumentBody";
import ProposalVideoPlayer from "@/components/public/ProposalVideoPlayer";
import SignPanel from "@/components/public/SignPanel";
import StageProgress from "@/components/public/StageProgress";
import ScrollProgress from "@/components/public/ScrollProgress";

const FRAME = "max-w-[840px] mx-auto bg-white rounded-2xl shadow-xl ring-1 ring-black/5 overflow-hidden";

function otpStillValid(token) {
  try {
    const raw = localStorage.getItem(`cass_sign_${token}`);
    if (raw) { const { email, at } = JSON.parse(raw); return email && Date.now() - at < 600000; }
  } catch { /* ignore */ }
  return false;
}

export default function PublicProposal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [accessCode, setAccessCode] = useState("");
  const [unlocked, setUnlocked] = useState(true);
  const [stage, setStage] = useState(null); // package | proposal | loe | welcome
  const [pkg, setPkg] = useState(null);
  const [selOpt, setSelOpt] = useState([]);
  const [welcome, setWelcome] = useState(null);

  const stageFromData = (d) => {
    if (d.status === "accepted" && d.loe?.signed) return "welcome";
    if (d.status === "accepted") return "loe";
    if (d.structure === "multi_option" && !d.selected_option_id) return "options";
    const needsPackage = (d.web_pages || []).some((p) => p.type === "packages" && p.allow_client_choice);
    if (needsPackage && !d.selected_package && !pkg) return "package";
    return "proposal";
  };

  const load = (setStageToo = true) =>
    api.get(`/public/proposals/${token}`).then((r) => {
      const d = r.data;
      setData(d);
      setUnlocked(!d.requires_access_code);
      setPkg((p) => p || d.selected_package || null);
      setSelOpt(d.selected_optionals || []);
      if (setStageToo) setStage((s) => s || stageFromData(d));
      return d;
    }).catch((e) => setErr(e.response?.data?.detail || "Proposal not available"));

  useEffect(() => { load(); }, [token]);
  useEffect(() => { api.get("/public/welcome").then((r) => setWelcome(r.data)).catch(() => {}); }, []);

  if (err) return <div className="min-h-screen grid place-items-center text-cass-slate">{err}</div>;
  if (!data || !stage) return <div className="min-h-screen grid place-items-center text-cass-slate">Loading your proposal…</div>;

  const verifyAccess = async () => {
    try { await api.post(`/public/proposals/${token}/verify-access`, { access_code: accessCode }); setUnlocked(true); }
    catch { toast.error("Invalid access code"); }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen grid place-items-center bg-cass-navy text-white p-6">
        <div className="text-center max-w-sm">
          <Lock className="w-8 h-8 mx-auto mb-4 text-cass-teal" />
          <h1 className="font-display text-3xl mb-2">Secure proposal</h1>
          <p className="text-slate-400 text-sm mb-4">Enter the access code provided by CASS.</p>
          <Input type="password" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} className="text-white bg-white/10 border-white/30 placeholder:text-white/50 mb-3 text-center tracking-[0.4em]" data-testid="access-code-input" />
          <Button onClick={verifyAccess} className="w-full bg-cass-teal" data-testid="unlock-btn">Unlock</Button>
        </div>
      </div>
    );
  }

  const isRenewal = data.proposal_type === "renewal";
  const groupOf = (s) => (s.presentation === "optional" ? "optional" : s.presentation === "one_off" ? "one_off" : s.billing_frequency);
  const grouped = data.services.reduce((a, s) => { (a[groupOf(s)] ||= []).push(s); return a; }, {});
  const businessName = data.client_name || "your business";
  const welcomeAbs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API.replace("/api", "")}${u}`);

  const sig = data.signatory || {};
  const proposalSignatory = {
    name: sig.name || "", job_title: sig.job_title || "",
    company: sig.company || data.client_name,
    email: data.recipient_email || sig.email || "",
  };
  const acc = data.accepted_signatory || data.signatory || {};
  const loeSignatory = {
    name: acc.name || "", job_title: acc.job_title || "",
    company: acc.company || data.client_name, email: acc.email || "",
  };
  const requireProposalOtp = !otpStillValid(token);

  // Package selection stage (dedicated, before signing)
  const pkgPage = (data.web_pages || []).find((p) => p.type === "packages" && p.allow_client_choice);
  const pickPackage = async (name) => {
    setPkg(name);
    try { await api.put(`/public/proposals/${token}/package`, { tier: name }); } catch { /* noop */ }
  };

  const selectOption = async (oid) => {
    try { await api.put(`/public/proposals/${token}/select-option`, { option_id: oid }); await load(false); setStage("proposal"); }
    catch { toast.error("Could not select that option — please try again"); }
  };

  // ---- Header ----
  const Header = (
    <div className="fixed top-0 inset-x-0 z-30 bg-white/85 backdrop-blur-xl border-b border-border">
      <div className="max-w-[840px] mx-auto flex items-center justify-between px-6 py-3">
        <div className="font-alt font-extrabold text-cass-navy text-lg">CASS</div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-cass-slate hidden md:inline">{data.reference}</span>
          <span className="font-semibold text-cass-navy tabular">{gbp(data.totals.monthly_recurring)}/mo</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-cass-bone min-h-screen">
      {stage === "proposal" && <ScrollProgress />}
      {Header}
      <div className="pt-20 pb-40 px-4">
        <div className="max-w-[840px] mx-auto">
          <StageProgress stage={stage} />

          <AnimatePresence mode="wait">
            <motion.div key={stage}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: "easeOut" }}>

              {/* STAGE: multi-option comparison */}
              {stage === "options" && (
                <div className={FRAME + " p-8"} data-testid="stage-options">
                  <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-2">Your CASS options</div>
                  <h2 className="font-display text-3xl font-bold text-cass-navy mb-2">Choose the way of working that suits you</h2>
                  <p className="text-cass-slate mb-6">These are <b>alternatives</b> — please select one. The figures below are not added together; only your chosen option applies.</p>
                  <div className={`grid md:grid-cols-2 ${(data.options || []).length >= 3 ? "lg:grid-cols-3" : ""} gap-4`}>
                    {(data.options || []).map((o) => (
                      <div key={o.id} className="border border-border rounded-xl p-6 bg-white flex flex-col" data-testid={`client-option-${o.order}`}>
                        {o.recommended && <span className="self-start text-[11px] font-semibold text-cass-teal bg-cass-teal/10 rounded-full px-2.5 py-0.5 mb-2">★ Recommended by CASS</span>}
                        <h3 className="font-display text-2xl text-cass-navy">{o.title}</h3>
                        {o.description && <p className="text-sm text-cass-slate mt-1">{o.description}</p>}
                        <div className="mt-4 text-cass-navy"><span className="text-3xl font-bold tabular">{gbp(o.totals.monthly_recurring)}</span><span className="text-sm text-cass-slate">/mo</span></div>
                        {o.totals.one_off > 0 && <div className="text-sm text-cass-slate">{gbp(o.totals.one_off)} one-off</div>}
                        <ul className="mt-4 space-y-1 flex-1">
                          {(o.services || []).filter((s) => s.presentation !== "optional").map((s, i) => (
                            <li key={i} className="text-sm text-cass-slate flex gap-2"><span className="text-cass-teal">✓</span>{s.client_name}</li>
                          ))}
                        </ul>
                        <Button className="mt-5 bg-cass-navy text-white rounded-full h-11 hover:bg-cass-navy/90" onClick={() => selectOption(o.id)} data-testid={`select-option-${o.order}`}>
                          Select this option<ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* STAGE: package selection */}
              {stage === "package" && pkgPage && (
                <div className={FRAME + " p-8"} data-testid="stage-package">
                  <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-2">Choose your plan</div>
                  <h2 className="font-display text-3xl font-bold text-cass-navy mb-2">{pkgPage.title || "Select a package"}</h2>
                  {pkgPage.intro && <p className="text-cass-slate mb-6">{pkgPage.intro}</p>}
                  <div className="grid md:grid-cols-3 gap-4">
                    {pkgPage.tiers.map((tier, j) => {
                      const active = pkg === tier.name;
                      return (
                        <button key={j} type="button" onClick={() => pickPackage(tier.name)}
                          className={`text-left bg-white border rounded-xl p-6 transition-all ${active ? "border-cass-teal ring-2 ring-cass-teal" : "border-border hover:border-cass-navy"}`} data-testid={`pkg-choose-${j}`}>
                          {tier.highlighted && <span className="text-xs font-semibold text-cass-teal">MOST POPULAR</span>}
                          <h3 className="font-display text-2xl text-cass-navy">{tier.name}</h3>
                          <div className="text-cass-navy font-bold text-xl mt-1">{tier.price} <span className="text-sm text-cass-slate font-normal">{tier.frequency}</span></div>
                          <ul className="mt-4 space-y-1">{tier.features.map((f, k) => <li key={k} className="text-sm text-cass-slate flex gap-2"><span className="text-cass-teal">✓</span>{f}</li>)}</ul>
                          <div className={`mt-4 text-center text-sm font-semibold rounded-full py-2 ${active ? "bg-cass-teal text-white" : "bg-slate-100 text-cass-navy"}`}>{active ? "Selected" : "Choose"}</div>
                        </button>
                      );
                    })}
                  </div>
                  <Button className="mt-8 bg-cass-teal text-white rounded-full h-11 px-6" disabled={!pkg} onClick={() => setStage("proposal")} data-testid="package-continue-btn">
                    Continue to proposal<ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}

              {/* STAGE: proposal document */}
              {stage === "proposal" && (
                <>
                  <ProposalVideoPlayer video={data.video} token={token} />
                  <div className={FRAME} data-testid="stage-proposal">
                    <DocumentBody data={data} grouped={grouped} isRenewal={isRenewal} pkg={pkg}
                      selectedOptionals={selOpt} canSelect={data.status !== "accepted"}
                      onToggleOptional={async (sid) => {
                        const next = selOpt.includes(sid) ? selOpt.filter((x) => x !== sid) : [...selOpt, sid];
                        setSelOpt(next);
                        try { const r = await api.put(`/public/proposals/${token}/optionals`, { service_ids: next }); setData((d) => ({ ...d, totals: r.data.totals || d.totals })); }
                        catch { toast.error("Could not update selection"); }
                      }} />
                  </div>
                  <div className="max-w-[840px] mx-auto mt-4 text-center space-y-2">
                    <button className="text-xs text-cass-slate underline block mx-auto" onClick={() => window.open(`${API}/public/proposals/${token}/pdf`, "_blank")} data-testid="download-pdf-public">Download the proposal as a PDF</button>
                    {data.attachment && (
                      <a href={welcomeAbs(data.attachment.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-cass-teal underline" data-testid="public-attachment-link">
                        <FileSignature className="w-3.5 h-3.5" />{data.attachment.filename || "Supporting document"}
                      </a>
                    )}
                  </div>
                </>
              )}

              {/* STAGE: letter of engagement */}
              {stage === "loe" && (
                <div className={FRAME + " p-8"} data-testid="stage-loe">
                  <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-2"><FileSignature className="w-4 h-4" />Letter of Engagement</div>
                  <h2 className="font-display text-3xl font-bold text-cass-navy mb-3">Your engagement letter</h2>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 mb-6 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 shrink-0" />Thank you — your proposal is accepted. To activate your engagement, please review and sign the Letter of Engagement below.
                  </div>
                  <p className="text-cass-slate mb-4">Your Letter of Engagement is the formal contractual document covering the schedules of services, privacy notice and standard terms. Please read it in full before signing.</p>
                  <iframe title="Letter of Engagement" src={`${API}/public/proposals/${token}/loe/pdf`} className="w-full h-[70vh] rounded-lg border border-border bg-white" data-testid="loe-iframe" />
                  <div className="mt-3 text-center">
                    <button className="text-xs text-cass-slate underline" onClick={() => window.open(`${API}/public/proposals/${token}/loe/pdf`, "_blank")} data-testid="loe-open-newtab">Open the engagement letter in a new tab</button>
                  </div>
                </div>
              )}

              {/* STAGE: welcome */}
              {stage === "welcome" && (
                <div className={FRAME + " p-10 text-center"} data-testid="welcome-screen">
                  {welcome?.team_image_url && <img src={welcomeAbs(welcome.team_image_url)} alt="The CASS team" className="w-full max-h-72 object-cover rounded-xl mb-8" data-testid="welcome-team-image" />}
                  <ShieldCheck className="w-10 h-10 text-cass-teal mx-auto mb-4" />
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-cass-navy mb-5" data-testid="welcome-heading">{welcome?.heading || "Welcome to the CASS family"}</h3>
                  <p className="text-cass-slate whitespace-pre-line text-lg leading-relaxed max-w-xl mx-auto" data-testid="welcome-body">
                    {(welcome?.body || "We're delighted to welcome you to the CASS family.").replace(/{business_name}/g, businessName)}
                  </p>
                  {data.onboarding?.available && data.onboarding?.status !== "submitted" && (
                    <div className="mt-8 bg-cass-navy rounded-2xl p-6 text-left" data-testid="welcome-onboarding-card">
                      <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-1">One quick step</div>
                      <h4 className="font-display text-2xl text-white mb-1">Let's get you set up</h4>
                      <p className="text-slate-300 text-sm mb-4">Complete your secure onboarding form so we can start work. It's pre-filled with what we already hold and you can save and resume any time.</p>
                      <Button className="bg-cass-teal text-white rounded-full px-6 h-11" onClick={() => { window.location.href = `/onboard/${token}`; }} data-testid="welcome-start-onboarding">
                        Start your onboarding<ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                  {data.onboarding?.status === "submitted" && (
                    <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 flex items-center gap-2 justify-center" data-testid="welcome-onboarding-done">
                      <ShieldCheck className="w-4 h-4" />Thanks — your onboarding details have been received.
                    </div>
                  )}
                  <Button className="mt-8 bg-cass-teal text-white rounded-full px-8 h-12 text-base hover:bg-cass-teal/90" onClick={() => { window.location.href = welcome?.cta_url || "https://www.cassonline.co.uk"; }} data-testid="welcome-continue-btn">
                    {welcome?.cta_label || "Click here to continue"}<ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                    <button className="text-xs text-cass-slate underline" onClick={() => window.open(`${API}/public/proposals/${token}/pdf`, "_blank")} data-testid="download-signed-pdf">Download your signed proposal</button>
                    <button className="text-xs text-cass-slate underline" onClick={() => window.open(`${API}/public/proposals/${token}/loe/pdf`, "_blank")} data-testid="loe-download-signed">Download your signed engagement letter</button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Sign panels are fixed-position and must live outside the animated (transformed) wrapper */}
          {stage === "proposal" && (
            <div className="max-w-[840px] mx-auto mt-4 flex items-center justify-center gap-1 text-xs" data-testid="client-timeline">
              {[["Sent", true], ["Viewed", ["viewed", "acceptance_started", "accepted"].includes(data.status)], ["Signed", data.status === "accepted"]].map(([lbl, done], i, arr) => (
                <div key={lbl} className="flex items-center gap-1">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-black/5 text-cass-slate"}`}>
                    <span className={`w-4 h-4 rounded-full grid place-items-center text-[10px] ${done ? "bg-emerald-600 text-white" : "bg-cass-slate/40 text-white"}`}>{done ? "\u2713" : i + 1}</span>{lbl}
                  </span>
                  {i < arr.length - 1 && <span className={`w-6 h-px ${done ? "bg-emerald-400" : "bg-border"}`} />}
                </div>
              ))}
            </div>
          )}
          {stage === "proposal" && data?.is_multi_sign && (
            <div className="max-w-[840px] mx-auto mt-4 rounded-md border border-border bg-white p-3" data-testid="signing-progress">
              <div className="text-xs font-semibold text-cass-navy mb-1">Signing progress — {data.signing_policy === "all" ? "all signatories must sign" : "any one can approve"}</div>
              <div className="flex flex-wrap gap-2">
                {(data.signers_progress || []).map((s, i) => (
                  <span key={i} className={`text-xs px-2 py-0.5 rounded-full border ${s.signed ? "bg-green-50 border-green-300 text-green-700" : "border-border text-cass-slate"}`}>
                    {s.signed ? "\u2713 " : "\u25CB "}{s.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {stage === "proposal" && (
            <SignPanel token={token} mode="proposal" requireOtp={requireProposalOtp} signatory={proposalSignatory} packageId={pkg}
              alignment={data.fee_alignment_choice}
              onDone={async () => { const d = await load(false); setStage(d?.status === "accepted" ? "loe" : "proposal"); }} />
          )}
          {stage === "loe" && (
            <SignPanel token={token} mode="loe" requireOtp={false} signatory={loeSignatory} statements={data.loe?.statements || []}
              onDone={() => setStage("welcome")} />
          )}
        </div>
      </div>

      {/* Footer contact */}
      <footer className="py-10 px-6 text-center border-t border-border bg-cass-bone">
        <p className="text-cass-slate text-sm">01827 780550 · support@cass-online.co.uk</p>
        <p className="text-cass-slate text-xs mt-1">3 Pebble Close Business Village, Amington, Tamworth, Staffordshire, B77 4RD</p>
      </footer>
    </div>
  );
}
